import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import AsyncSelect from "react-select/async";
import type { FilterOptionOption, SingleValue } from "react-select";
import Icon from "components/Icon";
import Loader from "components/Loader";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import type {
	CompetitionEditionDetailResponse,
	CompetitionEditionRowResponse,
	CompetitionEditionTeamResponse,
} from "libs/apiClient";
import { isGranted } from "pages/productManagement/functions";
import { Permission } from "pages/productManagement/types";
import { useToast } from "ui-kit";
import { CompetitionEditionFormModal } from "./CompetitionEditionFormModal";
import {
	competitionTypeLabel,
	formatCompetitionFormat,
	resolveCompetitionLogoUrl,
} from "./competitionOptions";
import "./competitions.css";

type TeamOption = {
	value: number;
	label: string;
	logoUrl?: string | null;
	country?: string | null;
	clubName?: string | null;
	ageCategory?: string | null;
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

function formatDates(startDate?: string | null, endDate?: string | null): string {
	if ((startDate == null || startDate === "") && (endDate == null || endDate === "")) {
		return "Not set";
	}
	if (startDate != null && startDate !== "" && endDate != null && endDate !== "") {
		return `${startDate} – ${endDate}`;
	}
	if (startDate != null && startDate !== "") {
		return `From ${startDate}`;
	}
	return `Until ${endDate}`;
}

function toEditionFormModel(
	edition: CompetitionEditionDetailResponse,
): CompetitionEditionRowResponse {
	return {
		id: edition.id,
		competitionId: edition.competitionId,
		seasonId: edition.seasonId,
		seasonName: edition.seasonName,
		groupLabel: edition.groupLabel,
		phaseLabel: edition.phaseLabel,
		isScoutVisible: edition.isScoutVisible,
		startDate: edition.startDate,
		endDate: edition.endDate,
		notes: edition.notes,
		teamCount: edition.teamCount,
	};
}

function TeamMetaLine({
	team,
	countryLabelsByCode,
}: {
	team: CompetitionEditionTeamResponse;
	countryLabelsByCode: Record<string, string>;
}) {
	const parts: string[] = [];
	if (team.id != null) {
		parts.push(`#${team.id}`);
	}
	if (team.ageCategory != null && team.ageCategory !== "") {
		parts.push(team.ageCategory);
	}
	if (team.country != null && team.country !== "") {
		parts.push(countryLabelsByCode[team.country] ?? team.country);
	}
	if (team.clubName != null && team.clubName !== "") {
		parts.push(team.clubName);
	}
	return <span className="competition-edition-detail__teamMeta">{parts.join(" · ")}</span>;
}

export function CompetitionEditionDetail() {
	const { editionId: editionIdParam } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { addSuccessToast, addErrorToast } = useToast();
	const canEdit = isGranted("competition", Permission.EDIT);

	const editionId = Number(editionIdParam);
	const isValidId = Number.isFinite(editionId) && editionId > 0;

	const [showEditModal, setShowEditModal] = useState(false);
	const [teamPendingRemoval, setTeamPendingRemoval] = useState<CompetitionEditionTeamResponse | null>(
		null,
	);
	const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);

	const {
		data: editionResponse,
		isLoading,
		isError,
		refetch,
	} = client.getCompetitionEdition.useQuery(
		{ id: editionId },
		{ enabled: isValidId, retry: false },
	);

	const addTeamsMutation = client.postCompetitionEditionTeams.useMutation();
	const removeTeamMutation = client.deleteCompetitionEditionTeam.useMutation();
	const patchVisibilityMutation = client.patchCompetitionEditionScoutVisibility.useMutation();

	const edition = editionResponse?.data;

	const { data: continentsResponse } = client.getContinentCountry.useQuery({ retry: false });

	const countryLabelsByCode = useMemo(() => {
		const labels: Record<string, string> = { INT: "International" };
		for (const continent of continentsResponse?.data ?? []) {
			for (const country of continent.options ?? []) {
				if (country.value != null && country.label != null) {
					labels[country.value] = country.label;
				}
			}
		}
		return labels;
	}, [continentsResponse]);

	const enrolledTeamIds = useMemo(() => {
		const ids = new Set<number>();
		for (const team of edition?.teams ?? []) {
			if (team.id != null) {
				ids.add(team.id);
			}
		}
		return ids;
	}, [edition?.teams]);

	const teamOptionsCacheKey = useMemo(() => {
		const sortedIds = Array.from(enrolledTeamIds).sort((leftId, rightId) => leftId - rightId);
		return `enrolled:${sortedIds.join(",")}`;
	}, [enrolledTeamIds]);

	const invalidateEdition = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ["getCompetitionEdition", { id: editionId }] });
		if (edition?.competitionId != null) {
			await queryClient.invalidateQueries({
				queryKey: ["getCompetitionEditions", { id: edition.competitionId }],
			});
		}
		await queryClient.invalidateQueries({ queryKey: ["getCompetitions"] });
	}, [edition?.competitionId, editionId, queryClient]);

	useEffect(() => {
		if (isValidId === false) {
			navigate("/product-management/competitions", { replace: true });
		}
	}, [isValidId, navigate]);

	const loadTeamOptions = useCallback(
		async (inputValue: string): Promise<TeamOption[]> => {
			const search = inputValue.trim();
			if (search === "" || isValidId === false) {
				return [];
			}

			const response = await client.getCompetitionEditionTeamOptions.query({
				id: editionId,
				search,
				limit: 20,
			});
			const items = response.data?.items ?? [];
			return items
				.filter(
					(item) =>
						item.id != null && item.name != null && enrolledTeamIds.has(item.id) === false,
				)
				.map((item) => ({
					value: item.id as number,
					label: item.name as string,
					logoUrl: item.logoUrl,
					country: item.country,
					clubName: item.clubName,
					ageCategory: item.ageCategory,
				}));
		},
		[editionId, enrolledTeamIds, isValidId],
	);

	const filterAvailableTeamOption = useCallback(
		(option: FilterOptionOption<TeamOption>): boolean => {
			return enrolledTeamIds.has(option.data.value) === false;
		},
		[enrolledTeamIds],
	);

	const handleAddTeam = useCallback(
		async (option: SingleValue<TeamOption>) => {
			if (option == null || canEdit === false || isValidId === false) {
				return;
			}

			try {
				await addTeamsMutation.mutateAsync({
					id: editionId,
					body: { teamIds: [option.value] },
				});
				addSuccessToast("Team enrolled");
				await invalidateEdition();
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
			}
		},
		[
			addErrorToast,
			addSuccessToast,
			addTeamsMutation,
			canEdit,
			editionId,
			invalidateEdition,
			isValidId,
		],
	);

	const handleConfirmRemoveTeam = useCallback(async () => {
		if (teamPendingRemoval?.id == null || canEdit === false || isValidId === false) {
			return;
		}

		try {
			await removeTeamMutation.mutateAsync({
				id: editionId,
				teamId: teamPendingRemoval.id,
			});
			addSuccessToast("Team removed");
			setTeamPendingRemoval(null);
			await invalidateEdition();
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		}
	}, [
		addErrorToast,
		addSuccessToast,
		canEdit,
		editionId,
		invalidateEdition,
		isValidId,
		removeTeamMutation,
		teamPendingRemoval,
	]);

	const handleToggleHide = useCallback(async () => {
		if (edition == null || canEdit === false || isValidId === false || isVisibilityUpdating) {
			return;
		}

		const nextIsScoutVisible = edition.isScoutVisible !== true;
		setIsVisibilityUpdating(true);
		try {
			await patchVisibilityMutation.mutateAsync({
				id: editionId,
				body: { isScoutVisible: nextIsScoutVisible },
			});
			addSuccessToast(nextIsScoutVisible ? "Edition shown in Scout" : "Edition hidden from Scout");
			await invalidateEdition();
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
		} finally {
			setIsVisibilityUpdating(false);
		}
	}, [
		addErrorToast,
		addSuccessToast,
		canEdit,
		edition,
		editionId,
		invalidateEdition,
		isValidId,
		isVisibilityUpdating,
		patchVisibilityMutation,
	]);

	if (isValidId === false) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="competition-edition-detail">
				<Loader />
			</div>
		);
	}

	if (isError || edition == null) {
		return (
			<div className="competition-edition-detail">
				<p>Failed to load competition edition.</p>
				<button type="button" className="btn btn-light-blue" onClick={() => refetch()}>
					Retry
				</button>
				<Link to="/product-management/competitions" className="btn btn--cancel">
					Back to competitions
				</Link>
			</div>
		);
	}

	const teams = edition.teams ?? [];
	const teamCount = edition.teamCount ?? teams.length;
	const isScoutVisible = edition.isScoutVisible !== false;
	const isHidden = isScoutVisible === false;
	const countryLabel =
		countryLabelsByCode[edition.competitionCountry ?? ""] ?? edition.competitionCountry ?? "-";
	const groupLabel =
		edition.groupLabel != null && edition.groupLabel !== "" ? edition.groupLabel : "-";
	const phaseLabel =
		edition.phaseLabel != null && edition.phaseLabel !== "" ? edition.phaseLabel : "-";
	const matchCount = edition.matchCount ?? 0;
	const formatLabel = formatCompetitionFormat(
		edition.competitionFormat,
		edition.competitionMatchLengthMinutes,
	);
	const competitionLogoSrc = resolveCompetitionLogoUrl(edition.competitionLogoUrl);

	return (
		<div className="competition-edition-detail">
			<div className="competitions-page__breadcrumbs">
				<Link to="/product-management/competitions">Leagues & Tournaments</Link>
				<span className="competitions-page__breadcrumbsPipe">/</span>
				<Link to="/product-management/competitions">Competitions</Link>
				<span className="competitions-page__breadcrumbsPipe">/</span>
				<span className="competitions-page__breadcrumbsCurrent">Edition detail</span>
			</div>

			<header className="competition-edition-detail__header">
				{competitionLogoSrc !== "" ? (
					<img
						src={competitionLogoSrc}
						alt=""
						className="competition-edition-detail__logo"
					/>
				) : (
					<span className="competition-edition-detail__logo competition-edition-detail__logo--placeholder" />
				)}
				<div className="competition-edition-detail__headerText">
					<h1 className="competition-edition-detail__title">
						{edition.competitionName ?? "Competition edition"}
					</h1>
					<div className="competition-edition-detail__badges">
						<span className="competitions-badge competitions-badge--meta">
							{edition.seasonName ?? "-"}
						</span>
						{groupLabel !== "-" ? (
							<span className="competitions-badge competitions-badge--meta">{groupLabel}</span>
						) : null}
						<span className="competitions-badge competitions-badge--meta">
							#{edition.id ?? "-"}
						</span>
						<span className="competitions-badge competitions-badge--muted">
							{competitionTypeLabel(edition.competitionType)}
						</span>
						{formatLabel !== "-" ? (
							<span className="competitions-badge competitions-badge--muted">{formatLabel}</span>
						) : null}
						<span
							className={`competitions-visibility-badge ${
								isScoutVisible ? "is-visible" : "is-hidden"
							}`}
						>
							<span
								className={`icon icon-new icon-eye ${
									isScoutVisible ? "icon-success" : "icon-danger"
								}`}
								aria-hidden="true"
							/>
							{isScoutVisible ? "Scout visible" : "Hidden"}
						</span>
					</div>
				</div>
			</header>

			<div className="competition-edition-detail__grid">
				<section className="competition-edition-detail__card">
					<div className="competition-edition-detail__cardHeader">
						<h2 className="competition-edition-detail__cardTitle">Team membership</h2>
						<span className="competitions-badge competitions-badge--muted">
							{teamCount} team{teamCount === 1 ? "" : "s"}
						</span>
					</div>

					{canEdit ? (
						<div className="competition-edition-detail__search">
							<span
								className="icon icon-new icon-16 icon-dark-grey icon-search competition-edition-detail__searchIcon"
								aria-hidden="true"
							/>
							<AsyncSelect<TeamOption>
								// cacheOptions is a boolean in react-select 5. Remount so a removed team is searchable again.
								key={teamOptionsCacheKey}
								classNamePrefix="competition-edition-team-select"
								className="competition-edition-detail__asyncSelect"
								cacheOptions
								defaultOptions={false}
								loadOptions={loadTeamOptions}
								filterOption={filterAvailableTeamOption}
								value={null}
								onChange={handleAddTeam}
								placeholder="Search teams to add them to the competition..."
								isClearable={false}
								isDisabled={addTeamsMutation.isLoading}
								noOptionsMessage={({ inputValue }) => {
									if (inputValue.trim() === "") {
										return "Type to search teams";
									}
									return "No teams found";
								}}
								formatOptionLabel={(option) => {
									const countryPart =
										option.country != null && option.country !== ""
											? (countryLabelsByCode[option.country] ?? option.country)
											: null;
									return (
										<div className="competition-edition-detail__option">
											{option.logoUrl != null && option.logoUrl !== "" ? (
												<img src={option.logoUrl} alt="" className="competitions-logo" />
											) : (
												<span className="competitions-logo competitions-logo--placeholder" />
											)}
											<div>
												<div className="competition-edition-detail__optionName">{option.label}</div>
												<div className="competition-edition-detail__optionMeta">
													{[
														`#${option.value}`,
														option.ageCategory,
														countryPart,
														option.clubName,
													]
														.filter((part) => part != null && part !== "")
														.join(" · ")}
												</div>
											</div>
										</div>
									);
								}}
							/>
						</div>
					) : null}

					{teams.length === 0 ? (
						<p className="competition-edition-detail__empty">No teams enrolled yet.</p>
					) : (
						<ul className="competition-edition-detail__teamList">
							{teams.map((team) => {
								const teamKey = team.id ?? team.name ?? "team";
								return (
									<li key={teamKey} className="competition-edition-detail__teamRow">
										{team.logoUrl != null && team.logoUrl !== "" ? (
											<img src={team.logoUrl} alt="" className="competitions-logo" />
										) : (
											<span className="competitions-logo competitions-logo--placeholder" />
										)}
										<div className="competition-edition-detail__teamInfo">
											<span className="competition-edition-detail__teamName">
												{team.name ?? "-"}
											</span>
											<TeamMetaLine team={team} countryLabelsByCode={countryLabelsByCode} />
										</div>
										{canEdit ? (
											<button
												type="button"
												className="competition-edition-detail__removeBtn"
												aria-label={`Remove ${team.name ?? "team"}`}
												disabled={removeTeamMutation.isLoading}
												onClick={() => setTeamPendingRemoval(team)}
											>
												<Icon icon="cross" />
											</button>
										) : null}
									</li>
								);
							})}
						</ul>
					)}
				</section>

				<section className="competition-edition-detail__card">
					<div className="competition-edition-detail__cardHeader">
						<h2 className="competition-edition-detail__cardTitle">Competition details</h2>
					</div>

					<dl className="competition-edition-detail__details">
						<div>
							<dt>Season</dt>
							<dd>{edition.seasonName ?? "-"}</dd>
						</div>
						<div>
							<dt>Group</dt>
							<dd>{groupLabel}</dd>
						</div>
						<div>
							<dt>Matches tagged</dt>
							<dd>{matchCount > 0 ? matchCount : "—"}</dd>
						</div>
						<div>
							<dt>Dates</dt>
							<dd>{formatDates(edition.startDate, edition.endDate)}</dd>
						</div>
						<div>
							<dt>Phase</dt>
							<dd>{phaseLabel}</dd>
						</div>
						<div>
							<dt>Country</dt>
							<dd>{countryLabel}</dd>
						</div>
					</dl>

					{canEdit ? (
						<div className="competition-edition-detail__hideBlock">
							<div>
								<strong>Hide this edition</strong>
								<p>
									Hides only the {edition.seasonName ?? "selected"} competition from Scout. Other
									editions of this competition stay visible.
								</p>
							</div>
							<label className="competition-edition-detail__switch">
								<input
									type="checkbox"
									checked={isHidden}
									disabled={isVisibilityUpdating}
									onChange={handleToggleHide}
								/>
								<span className="competition-edition-detail__switchSlider" />
							</label>
						</div>
					) : null}

					{canEdit ? (
						<button
							type="button"
							className="competition-edition-detail__editBtn"
							onClick={() => setShowEditModal(true)}
						>
							<span className="icon icon-new icon-16 icon-dark-grey icon-pencil" aria-hidden="true" />
							Edit edition
						</button>
					) : null}
				</section>
			</div>

			{showEditModal ? (
				<CompetitionEditionFormModal
					competitionId={edition.competitionId ?? 0}
					hasGroups={edition.hasGroups === true}
					hasPhases={edition.hasPhases === true}
					edition={toEditionFormModel(edition)}
					onClose={() => setShowEditModal(false)}
					onSaved={invalidateEdition}
				/>
			) : null}

			{teamPendingRemoval != null ? (
				<div className="modal open">
					<span className="modal__backdrop" onClick={() => setTeamPendingRemoval(null)} />
					<div className="modal__container modal__container--sm">
						<span className="modal__close" onClick={() => setTeamPendingRemoval(null)}>
							<Icon icon="cross" />
						</span>
						<div className="modal__body text-center">
							<div className="modal-msg">
								<p>
									<Icon className="modal-icon" icon="warning-new" size={50} />
								</p>
								<h6>Remove team from edition?</h6>
								<p>
									Remove <strong>{teamPendingRemoval.name}</strong> from this competition edition.
								</p>
								{(teamPendingRemoval.assignedMatchCount ?? 0) > 0 ? (
									<p className="competition-edition-detail__warning">
										This team has {teamPendingRemoval.assignedMatchCount} match
										{(teamPendingRemoval.assignedMatchCount ?? 0) === 1 ? "" : "es"} already
										assigned to this competition.
									</p>
								) : null}
								<p>
									<button
										type="button"
										className="btn btn--red"
										disabled={removeTeamMutation.isLoading}
										onClick={handleConfirmRemoveTeam}
									>
										Remove team
									</button>
									<button
										type="button"
										className="btn btn--cancel"
										onClick={() => setTeamPendingRemoval(null)}
									>
										Cancel
									</button>
								</p>
							</div>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

export default CompetitionEditionDetail;
